import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Snake (React + Canvas)
 * - Starts when player presses Space / Click / Arrow / WASD
 * - Arrow/WASD controls
 * - Fixed-timestep loop via rAF (speed in ms per tick)
 * - High score persisted in localStorage ("snakeHighScore")
 * - Pixel-perfect scaling for crisp rendering on all DPIs
 * - Centers nicely in modal; canvas rescales to container width
 */
export default function Snake() {
  // Canvas & loop refs
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  // Logical grid
  const COLS = 20;
  const ROWS = 20;

  // Game state
  const [score, setScore] = useState(0);
  const [high, setHigh] = useState(() => {
    const saved = localStorage.getItem("snakeHighScore");
    return saved ? parseInt(saved, 10) : 0;
  });
  const [gameOver, setGameOver] = useState(false);
  const [started, setStarted] = useState(false);
  const startedRef = useRef(false);
  const gameOverRef = useRef(false);

  // Snake and food stored in refs to avoid re-renders every tick
  const snakeRef = useRef({
    body: [{ x: 10, y: 10 }], // head is body[0]
    dir: { x: 1, y: 0 },      // moving right
    nextDir: { x: 1, y: 0 },  // buffered input
    grew: false,
  });
  const foodRef = useRef({ x: 5, y: 5 });

  // Timing (fixed timestep)
  const baseSpeedMs = 140;         // initial ms per step (lower = faster)
  const speedRef = useRef(baseSpeedMs);
  const lastTimeRef = useRef(0);
  const accRef = useRef(0);

  // Helpers ----------------------------------------------------

  const randomEmptyCell = useCallback(() => {
    const occupied = new Set(
      snakeRef.current.body.map(p => `${p.x},${p.y}`)
    );
    let x, y;
    do {
      x = Math.floor(Math.random() * COLS);
      y = Math.floor(Math.random() * ROWS);
    } while (occupied.has(`${x},${y}`));
    return { x, y };
  }, [COLS, ROWS]);

  

  // Drawing with crisp scaling --------------------------------
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    // Responsive pixel scaling
    const cssSize = Math.min(480, Math.min(window.innerWidth * 0.9, 560));
    const dpr = window.devicePixelRatio || 1;
    canvas.style.width = `${cssSize}px`;
    canvas.style.height = `${cssSize}px`;
    canvas.width = Math.floor(cssSize * dpr);
    canvas.height = Math.floor(cssSize * dpr);

    const cellSize = Math.floor(canvas.width / COLS);
    const offsetX = Math.floor((canvas.width - cellSize * COLS) / 2);
    const offsetY = Math.floor((canvas.height - cellSize * ROWS) / 2);

    // Clear
    ctx.fillStyle = "#0f172a"; // slate-900
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Grid (subtle)
    ctx.strokeStyle = "rgba(255,255,255,0.06)";
    ctx.lineWidth = Math.max(1, Math.floor(dpr));
    for (let x = 0; x <= COLS; x++) {
      const gx = offsetX + x * cellSize + 0.5;
      ctx.beginPath();
      ctx.moveTo(gx, offsetY + 0.5);
      ctx.lineTo(gx, offsetY + ROWS * cellSize + 0.5);
      ctx.stroke();
    }
    for (let y = 0; y <= ROWS; y++) {
      const gy = offsetY + y * cellSize + 0.5;
      ctx.beginPath();
      ctx.moveTo(offsetX + 0.5, gy);
      ctx.lineTo(offsetX + COLS * cellSize + 0.5, gy);
      ctx.stroke();
    }

    // Food
    const food = foodRef.current;
    ctx.fillStyle = "#ef4444"; // red-500
    ctx.fillRect(
      offsetX + food.x * cellSize + 2 * dpr,
      offsetY + food.y * cellSize + 2 * dpr,
      cellSize - 4 * dpr,
      cellSize - 4 * dpr
    );

    // Snake
    const { body } = snakeRef.current;
    body.forEach((seg, i) => {
      ctx.fillStyle = i === 0 ? "#22d3ee" : "#06b6d4"; // head brighter
      ctx.fillRect(
        offsetX + seg.x * cellSize + 1 * dpr,
        offsetY + seg.y * cellSize + 1 * dpr,
        cellSize - 2 * dpr,
        cellSize - 2 * dpr
      );
    });

    // Overlay hints
    if (!startedRef.current && !gameOverRef.current) {
      ctx.fillStyle = "rgba(255,255,255,0.9)";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = `${16 * dpr}px system-ui, -apple-system, Segoe UI, Roboto`;
      ctx.fillText(
        "Press Space / Click to Start",
        canvas.width / 2,
        canvas.height / 2
      );
    }

    if (gameOverRef.current) {
      ctx.fillStyle = "rgba(0,0,0,0.5)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = "#fff";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = `${20 * dpr}px system-ui, -apple-system, Segoe UI, Roboto`;
      ctx.fillText("Game Over", canvas.width / 2, canvas.height / 2 - 20 * dpr);
      ctx.font = `${14 * dpr}px system-ui, -apple-system, Segoe UI, Roboto`;
      ctx.fillText(
        "Press R to Restart",
        canvas.width / 2,
        canvas.height / 2 + 8 * dpr
      );
    }
  }, [COLS, ROWS]);

  // Single simulation step ------------------------------------
  const step = useCallback(() => {
    if (!startedRef.current || gameOverRef.current) return;

    // Apply buffered direction (prevent reverse)
    const s = snakeRef.current;
    const cur = s.dir;
    const nd = s.nextDir;
    const reversing = cur.x + nd.x === 0 && cur.y + nd.y === 0;
    if (!reversing) {
      s.dir = nd;
    }

    const head = s.body[0];
    const newHead = { x: head.x + s.dir.x, y: head.y + s.dir.y };

    // Collisions: walls
    if (
      newHead.x < 0 ||
      newHead.x >= COLS ||
      newHead.y < 0 ||
      newHead.y >= ROWS
    ) {
      gameOverRef.current = true;
      setGameOver(true);
      return;
    }

    // Collisions: self
    const hitSelf = s.body.some((seg) => seg.x === newHead.x && seg.y === newHead.y);
    if (hitSelf) {
      gameOverRef.current = true;
      setGameOver(true);
      return;
    }

    // Move
    s.body.unshift(newHead);

    // Eat food?
    if (newHead.x === foodRef.current.x && newHead.y === foodRef.current.y) {
      s.grew = true;
      setScore((prev) => {
        const next = prev + 1;
        // Difficulty ramp: speed up every 4 apples (min floor)
        if (next % 4 === 0) {
          speedRef.current = Math.max(70, speedRef.current - 10);
        }
        // High score
        setHigh((h) => {
          if (next > h) {
            localStorage.setItem("snakeHighScore", String(next));
            return next;
          }
          return h;
        });
        return next;
      });
      foodRef.current = randomEmptyCell();
    }

    // Trim tail unless we grew
    if (s.grew) {
      s.grew = false;
    } else {
      s.body.pop();
    }
  }, [COLS, ROWS, randomEmptyCell]);

  // Main loop --------------------------------------------------
  const loop = useCallback((time) => {
    if (!lastTimeRef.current) lastTimeRef.current = time;
    const delta = time - lastTimeRef.current;
    lastTimeRef.current = time;

    accRef.current += delta;
    while (accRef.current >= speedRef.current) {
      step();
      accRef.current -= speedRef.current;
    }

    draw();
    animationRef.current = requestAnimationFrame(loop);
  }, [draw, step]);

  const resetGame = useCallback(() => {
    cancelAnimationFrame(animationRef.current);
    snakeRef.current = {
      body: [{ x: Math.floor(COLS / 2), y: Math.floor(ROWS / 2) }],
      dir: { x: 1, y: 0 },
      nextDir: { x: 1, y: 0 },
      grew: false,
    };
    foodRef.current = randomEmptyCell();
    speedRef.current = baseSpeedMs;
    lastTimeRef.current = 0;
    accRef.current = 0;
    setScore(0);
    setGameOver(false);
    setStarted(false);
    startedRef.current = false;
    gameOverRef.current = false;
    animationRef.current = requestAnimationFrame(loop);
  }, [COLS, ROWS, randomEmptyCell, loop]);

  // Input handlers --------------------------------------------
  const startIfNeeded = useCallback(() => {
    if (!startedRef.current && !gameOverRef.current) {
      startedRef.current = true;
      setStarted(true);
    }
  }, []);

  const handleKey = useCallback(
    (e) => {
      const { key, code } = e;
      if (gameOverRef.current) {
        if (key.toLowerCase() === "r") {
          resetGame();
        }
        return;
      }

      // Start
      if (code === "Space") {
        startIfNeeded();
        return;
      }

      // Direction
      const s = snakeRef.current;
      if (key === "ArrowUp" || key.toLowerCase() === "w") {
        s.nextDir = { x: 0, y: -1 };
        startIfNeeded();
      } else if (key === "ArrowDown" || key.toLowerCase() === "s") {
        s.nextDir = { x: 0, y: 1 };
        startIfNeeded();
      } else if (key === "ArrowLeft" || key.toLowerCase() === "a") {
        s.nextDir = { x: -1, y: 0 };
        startIfNeeded();
      } else if (key === "ArrowRight" || key.toLowerCase() === "d") {
        s.nextDir = { x: 1, y: 0 };
        startIfNeeded();
      } else if (key.toLowerCase() === "r") {
        resetGame();
      }
    },
    [resetGame, startIfNeeded]
  );

  const handleClick = useCallback(() => {
    // Click to start on mobile / mouse
    if (!startedRef.current && !gameOverRef.current) startIfNeeded();
  }, [startIfNeeded]);

  // Setup / teardown ------------------------------------------
  useEffect(() => {
    resetGame(); // initializes board, food, and kicks off rAF loop

    window.addEventListener("keydown", handleKey);
    window.addEventListener("resize", draw);
    const canvas = canvasRef.current;
    canvas.addEventListener("click", handleClick);

    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener("keydown", handleKey);
      window.removeEventListener("resize", draw);
      canvas.removeEventListener("click", handleClick);
    };
  }, [handleClick, handleKey, draw, resetGame]);

  // UI ---------------------------------------------------------
  return (
    <div style={{ textAlign: "center" }}>
      <canvas
        ref={canvasRef}
        style={{
          display: "block",
          margin: "0 auto",
          borderRadius: 8,
          background: "#0f172a",
          boxShadow: "0 6px 30px rgba(0,0,0,0.3)",
        }}
      />
      <div style={{ marginTop: 10, color: "#000000ff" }}>
        <strong>Score:</strong> {score} &nbsp; | &nbsp; <strong>High:</strong> {high}
      </div>

      {!started && !gameOver && (
        <div style={{ fontStyle: "italic", marginTop: 6, color: "#94a3b8" }}>
          Press Space / Click to Start &middot; Arrow Keys / WASD to move
        </div>
      )}

      {gameOver && (
        <div style={{ marginTop: 12 }}>
          <button
            onClick={resetGame}
            style={{
              padding: "8px 16px",
              fontSize: 16,
              background: "linear-gradient(90deg, #22d3ee, #6366f1)",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              cursor: "pointer",
            }}
          >
            Restart
          </button>
        </div>
      )}
    </div>
  );
}
